import React, { useState, useEffect } from "react";
import { Calendar } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { Modal, ModalHeader, ModalBody, Button } from "reactstrap";
import { toast } from "react-toastify";
import moment from "moment";
import "./StreakManagement.css";
import {
  useLazyGetUserStreakDataQuery,
  useUpdateUserStreakMutation,
} from "../../../../store/api/userManagementApi";

const StreakManagementModal = ({ isOpen, toggle, userEmail, userId }) => {
  const [triggerGetStreakData] = useLazyGetUserStreakDataQuery();
  const [updateStreakMutation] = useUpdateUserStreakMutation();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [streakData, setStreakData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [clickedDate, setClickedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const lastRequestedRangeRef = React.useRef(null);
  const prevIsOpenRef = React.useRef(false);

  useEffect(() => {
    if (isOpen && userId) {
      const visibleStart = moment(currentMonth).startOf('month').startOf('week').format('YYYY-MM-DD');
      const visibleEnd = moment(currentMonth).endOf('month').endOf('week').format('YYYY-MM-DD');
      const wasOpen = prevIsOpenRef.current;
      const showLoading = !wasOpen;
      loadStreakData({ startDate: visibleStart, endDate: visibleEnd }, showLoading);
    } else if (!isOpen) {
      lastRequestedRangeRef.current = null;
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, userId, currentMonth]);

  useEffect(() => {
    if (!isOpen || !streakData) return;

    const handleClick = (e) => {
      const button = e.currentTarget;
      const dateText = button.textContent?.trim();
      if (!dateText || isNaN(dateText)) return;

      const clickedDate = moment(currentMonth).date(parseInt(dateText)).toDate();
      const dateString = moment(clickedDate).format('YYYY-MM-DD');
      const dayData = streakData?.calendar_data?.[dateString];
      
      if (dayData?.watched === false || !dayData?.watched) {
        e.preventDefault();
        e.stopPropagation();
        setClickedDate(clickedDate);
        setShowConfirmModal(true);
      }
    };

    const attachClickListeners = () => {
      const rdrDayButtons = document.querySelectorAll('.custom-streak-calendar .rdrDay');
      
      rdrDayButtons.forEach((button) => {
        if (!button._streakClickAttached) {
          button.addEventListener('click', handleClick);
          button._streakClickAttached = true;
        }
      });
    };

    const timeoutId = setTimeout(attachClickListeners, 100);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          attachClickListeners();
        }
      });
    });

    const calendarContainer = document.querySelector('.custom-streak-calendar');
    if (calendarContainer) {
      observer.observe(calendarContainer, { 
        childList: true, 
        subtree: true 
      });
    }

    const monthHeaderObserver = new MutationObserver(() => {
      const monthHeader = document.querySelector('.custom-streak-calendar .rdrMonth .rdrMonthName');
      if (monthHeader) {
        const text = monthHeader.textContent.trim();
        const parsed = moment(text, 'MMMM YYYY');
        if (parsed.isValid() && !parsed.isSame(currentMonth, 'month')) {
          handleMonthChange(parsed.toDate());
        }
      }
    });
    const monthHeaderContainer = document.querySelector('.custom-streak-calendar .rdrMonth .rdrMonthName');
    if (monthHeaderContainer) {
      monthHeaderObserver.observe(monthHeaderContainer, { childList: true, characterData: true, subtree: true });
    }

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
      monthHeaderObserver.disconnect();
      
      const rdrDayButtons = document.querySelectorAll('.custom-streak-calendar .rdrDay');
      rdrDayButtons.forEach((button) => {
        if (button._streakClickAttached) {
          button.removeEventListener('click', handleClick);
          delete button._streakClickAttached;
        }
      });
    };
  }, [isOpen, streakData, currentMonth]);

  const loadStreakData = async (rangeOrDate, showLoading = false, force = false) => {
    let startDate;
    let endDate;
    if (rangeOrDate && typeof rangeOrDate === 'object' && rangeOrDate.startDate && rangeOrDate.endDate) {
      startDate = rangeOrDate.startDate;
      endDate = rangeOrDate.endDate;
    } else {
      startDate = moment(currentMonth).startOf('month').startOf('week').format('YYYY-MM-DD');
      endDate = moment(currentMonth).endOf('month').endOf('week').format('YYYY-MM-DD');
    }

    const rangeKey = `${startDate}:${endDate}`;
    if (!force && lastRequestedRangeRef.current === rangeKey) {
      return;
    }

    if (showLoading) setLoading(true);
    try {
      const apiResponse = await triggerGetStreakData({ userId, startDate, endDate }).unwrap();

      const calendar_data = {};
      apiResponse.data.forEach(item => {
        calendar_data[item.date] = {
          watched: item.watched,
          is_streak_start: item.is_streak_start,
          is_streak_end: item.is_streak_end,
          is_break: item.is_break
        };
      });
      
      const transformedData = {
        current_streak: parseInt(apiResponse.user_streak.current_streak) || 0,
        best_streak: parseInt(apiResponse.user_streak.best_streak) || 0,
        total_days: parseInt(apiResponse.user_streak.total_days) || 0,
        calendar_data
      };
      
      setStreakData(transformedData);
    } catch (error) {
      toast.error("Failed to load streak data", { position: "bottom-center" });
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleDateClick = (date, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    const dateString = moment(date).format('YYYY-MM-DD');
    const dayData = streakData?.calendar_data?.[dateString];
    
    if (dayData?.watched === false || dayData?.watched === null || !dayData) {
      setClickedDate(date);
      setShowConfirmModal(true);
    }
  };

  const handleConfirmStreakUpdate = async () => {
    setShowConfirmModal(false);
    try {
      await updateStreakMutation({ userId, date: moment(clickedDate).format('YYYY-MM-DD') }).unwrap();
      toast.success("Streak updated successfully", { position: "bottom-center" });
      await loadStreakData(null, false, true);
    } catch (error) {
      toast.error("Failed to update streak", { position: "bottom-center" });
    }
    setClickedDate(null);
  };

  const renderDay = (date) => {
    const dateString = moment(date).format('YYYY-MM-DD');
    const dayData = streakData?.calendar_data?.[dateString];
    const isCurrentMonth = moment(date).isSame(currentMonth, 'month');
    const isPreviousMonth = moment(date).isSame(moment(currentMonth).subtract(1, 'month'), 'month');
    
    if (!isCurrentMonth && !isPreviousMonth) return null;

    let className = 'streak-day';
    
    if (isPreviousMonth) {
      className += ' previous-month';
    }
    
    if (dayData?.watched === true) {
      className += ' streak-completed';
    } else if (dayData?.is_break === true) {
      className += ' streak-incomplete';
    }

    return (
      <div className={className}>
        {moment(date).date()}
      </div>
    );
  };

  const handleMonthChange = (date) => {
    if (!moment(date).isSame(currentMonth, 'month') || !moment(date).isSame(currentMonth, 'year')) {
      setCurrentMonth(date);
    }
    setSelectedDate(date);
  };

  return (
    <>
      <Modal 
        isOpen={isOpen} 
        toggle={toggle}
        size="xl"
        style={{ maxWidth: '95vw', width: '95vw' }}
        className="streak-management-modal"
      >
        <ModalHeader toggle={toggle} className="streak-modal-header">
          <div className="d-flex justify-content-between align-items-center w-100">
            <h4 className="mb-0">Streak Management - {userEmail || "—"}</h4>
          </div>
        </ModalHeader>
        
        <ModalBody className="streak-modal-body">
          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="row h-100">
              <div className="col-md-8 streak-calendar-section">
                <div className="calendar-container">
                  <Calendar
                    date={currentMonth}
                    shownDate={currentMonth}
                    onChange={handleMonthChange}
                    onShownDateChange={handleMonthChange}
                    showDateDisplay={false}
                    dayContentRenderer={renderDay}
                    className="custom-streak-calendar"
                  />
                </div>
              </div>
              
              <div className="col-md-4 streak-stats-section">
                <div className="stats-container">
                  <h5 className="stats-heading">Streaks</h5>
                  
                  <div className="stat-item">
                    <div className="stat-number">
                      {streakData?.current_streak || 0} {(streakData?.current_streak || 0) === 1 ? 'day' : 'days'}
                    </div>
                    <div className="stat-label">
                      Current Streak
                    </div>
                  </div>
                  
                  <div className="stat-item">
                    <div className="stat-number">
                      {streakData?.best_streak || 0} {(streakData?.best_streak || 0) === 1 ? 'day' : 'days'}
                    </div>
                    <div className="stat-label">
                      Best Streak
                    </div>
                  </div>
                  
                  <div className="stat-item">
                    <div className="stat-number">
                      {streakData?.total_days || 0} {(streakData?.total_days || 0) === 1 ? 'day' : 'days'}
                    </div>
                    <div className="stat-label">
                      Total Days
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={showConfirmModal} toggle={() => setShowConfirmModal(false)}>
        <ModalHeader toggle={() => setShowConfirmModal(false)}>
          Update Streak
        </ModalHeader>
        <ModalBody>
          Are you sure you want to update the streak for {moment(clickedDate).format('MMMM D, YYYY')} for user {userEmail}?
        </ModalBody>
        <div className="modal-footer">
          <Button color="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleConfirmStreakUpdate}>
            Yes, Update
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default StreakManagementModal;